/**
 * Helpers compartidos de selección de intervinientes (contrato / mapa).
 * Reutilizables desde ContractManagement y el flujo legacy.
 */

export type IntervenerIdentityLike =
{
    id?: number;
    status?: string;
    userId?: number;
};

/**
 * Interviniente prestado (propietario distinto) con KYC invalidado.
 * Solo el propietario puede re-firmarlo; no es seleccionable en el contrato.
 */
export function isBorrowedWithOutdatedKyc(userIdentity: IntervenerIdentityLike | null | undefined, currentUserId: number): boolean
{
    return userIdentity?.status === "OUTDATED"
        && userIdentity?.userId !== undefined
        && userIdentity.userId !== currentUserId;
}

/**
 * Estados seleccionables para el contrato: ACTIVE, PENDING y OUTDATED propio.
 * NEW, CANCELLED, etc. no lo son. Un prestado OUTDATED tampoco.
 */
export function isSelectable(userIdentity: IntervenerIdentityLike | null | undefined, currentUserId: number): boolean
{
    if(!userIdentity)
    {
        return false;
    }

    if(isBorrowedWithOutdatedKyc(userIdentity, currentUserId))
    {
        return false;
    }

    return userIdentity.status !== undefined && ["ACTIVE", "PENDING", "OUTDATED"].includes(userIdentity.status);
}

export function isMinorBirthday(birthday: string | Date | null | undefined, now: Date = new Date()): boolean
{
    if(birthday == null)
    {
        return false;
    }

    const birth = new Date(birthday);
    if(Number.isNaN(birth.getTime()))
    {
        return false;
    }

    let age = now.getFullYear() - birth.getFullYear();
    const monthDiff = now.getMonth() - birth.getMonth();
    if(monthDiff < 0 || (monthDiff === 0 && now.getDate() < birth.getDate()))
    {
        age -= 1;
    }
    return age < 18;
}

/** Relaciones de SavingsPlanUserIdentity usadas en el flujo de contrato. */
export const IntervenerRelation =
{
    Owner: 1,
    Cotitular: 11,
    LegalRepresentative: 4,
    Authorized: 5,
} as const;

/**
 * Puede recibir un rol en el contrato. Dos motivos independientes para que no:
 * el backend marca `isSelectable = false` si su estado no admite entrar en contrato (un borrador NEW
 * no está sincronizado y la formalización fallaría tras firmar), y `canNotifyKycOwner = true` señala
 * a un prestado con el KYC caducado, que solo su propietario puede firmar.
 */
export function isIdentitySelectableForContract(identity: {isSelectable?: boolean; canNotifyKycOwner?: boolean | null}): boolean
{
    return identity.isSelectable !== false && identity.canNotifyKycOwner !== true;
}

/** Espejo de selección de la rama de paso a mayoría de edad. */
export const MinorToAdultSelectionStepKey = "2.4.2";

/**
 * Bloqueo de roles en 2.4.2. Los roles llegan ya resueltos: al clonar el plan, MinorToAdultHolder
 * convirtió a los representantes legales en autorizados y copió el resto tal cual. Lo único que
 * decide aquí el nuevo adulto es si esos autorizados siguen en el contrato o salen; titulares y
 * cotitulares no cambian de rol y no puede aparecer ningún rol nuevo que no sea autorizado.
 */
export function isRelationLockedForMinorToAdult(currentRelation: number | undefined, relation: number | undefined): boolean
{
    if(currentRelation === IntervenerRelation.Owner || currentRelation === IntervenerRelation.Cotitular)
    {
        return true;
    }

    if(relation === undefined || relation === IntervenerRelation.Authorized)
    {
        return false;
    }

    return relation !== currentRelation;
}